import React, { ReactNode, useMemo } from "react";
import styled from "styled-components";
import { useTypedSelector } from "store";
import { api } from "api/api";
import { ProcessState, ValidationProcess } from "types/process";
import { ActionButtonConfirmStatusWithPolling } from "components";
import { ShowValidationsButton } from "./ShowValidationsButton";
import { UseProcessPollingOptions } from "hooks/useProcessPolling";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Alert,
  DialogContentText,
  Stack,
  Box,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import {
  formatDuration,
  getElapsedMsFromProcess,
  getElapsedLabel,
} from "utils/process.util";

export interface ValidateProductlineButtonProps {
  disabled: boolean;
}

export const ValidateProductlineButton: React.FC<
  ValidateProductlineButtonProps
> = ({ disabled = false }) => {
  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id
  );

  // Local UI state

  /** BEGIN ActionButtonConfirmStatusWithPolling Implementation */

  /**
   * Reset local state when the dialog opens
   */
  const onDialogOpen = () => {
    return null;
  };

  /**
   * startRequest:
   *    - Called once when the user confirms the action (clicks OK/Export).
   *    - Should start a backend process and return the initial process object.
   *    - Throw to surface an immediate validation/setup error in the dialog.
   */
  const startRequest = async (): Promise<ValidationProcess> => {
    if (!productLineId) {
      throw new Error("No product line selected.");
    }

    const response = await api.validateAllAsync(productLineId);

    // Old code considered HTTP 202 as the expected “accepted” response
    // and surfaced an error otherwise; we’ll keep that behavior.
    if (response?.status !== 202) {
      const serverMessage =
        response?.data?.message ??
        "Unable to initiate the Validate Product Line process. The connection to the server may have been lost.";
      throw new Error(serverMessage);
    }

    // Return the initial process payload
    return response.data as ValidationProcess;
  };

  /**
   * pollRequest:
   *    - Polls the server for updated process state by process id.
   *    - Called repeatedly (pollIntervalMs) until the process reaches a terminal state.
   *    - Must return the latest process object by id.
   */
  const pollRequest = async (id: string): Promise<ValidationProcess> => {
    const process = await api
      .getProcess(id)
      .then((r) => r.data as ValidationProcess);
    return process;
  };

  /**
   * Called once after the process reaches a terminal state.
   * On success, downloads the generated export file.
   * onTerminal:
   *    - Called once after polling ends (terminal state reached).
   *    - Typical use: download output files, show toasts, do follow-up API calls.
   *    - Throw to mark the dialog as ERROR (ModalDialog will surface the error).
   */
  //const onTerminal = async (_process: ValidationProcess): Promise<void> => {
  // No UI state needed here; buildResultSection will render everything
  //};

  /**
   * Options object consumed by useProcessPolling
   */
  const processPollingOptions: UseProcessPollingOptions<
    ValidationProcess,
    ValidationProcess
  > = {
    startRequest,
    pollRequest,
    pollIntervalMs: 2000,
    //onTerminal,
  };

  /**
   * Build the content section of the dialog.
   * This is the information displayed to the user before the process is initiated
   */
  const contentSection = useMemo<ReactNode>(() => {
    return (
      <ContentStack>
        <DialogContentText>
          Clicking <strong>Validate</strong> will run a full validation of the
          selected product line including all properties and tables.
        </DialogContentText>
        <DialogContentText>
          This may take some time, especially on large productlines.
        </DialogContentText>
        <DialogContentText>
          To see any resulting validations, wait for the process to finish, then
          click <strong>Show Validations</strong> button.
        </DialogContentText>
        <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
          If you navigate away from this page while the validation process is
          running, it will still run in the background, but you will not be able
          to see its progress.
        </Alert>
      </ContentStack>
    );
  }, []);

  /** Results section */
  const buildResultSection = (process: ValidationProcess): ReactNode => {
    // Early error rendering
    if (process.processState === ProcessState.ERROR) {
      // Show error message in an Alert
      return (
        <Box>
          <Alert severity="error" variant="outlined">
            {process.message || "Validation failed."}
          </Alert>
        </Box>
      );
    }

    // Processed counts
    const propDone = process.propertyNumComplete ?? 0;
    const propTotal = process.propertyTotal ?? propDone ?? 0;

    const tableDone = process.tableNumComplete ?? 0;
    const tableTotal = process.tableTotal ?? tableDone ?? 0;

    // Validation counts
    const plValidations = process.productLineNumValidations ?? 0;
    const propValidations = process.propertyNumValidations ?? 0;
    const tableValidations = process.tableNumValidations ?? 0;

    const totalValidations = plValidations + propValidations + tableValidations;

    // Elapsed time
    const elapsedMs = getElapsedMsFromProcess(process);
    const durationLabel = formatDuration(elapsedMs);
    const elapsedLabel = getElapsedLabel();

    // Build header text with singular/plural handling
    const headerText =
      totalValidations === 0
        ? "Validation Complete: No problems found"
        : `Validation Complete: ${totalValidations} ${
            totalValidations === 1 ? "issue" : "issues"
          }
          found`;

    return (
      <Stack spacing={1.5}>
        {/* Header */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="primary.dark"
          gutterBottom
        >
          {headerText}
        </Typography>

        {/* Processed rows */}
        <Grid container spacing={1}>
          {/* Elapsed */}
          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              {elapsedLabel}
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography variant="body2" textAlign="right">
              {durationLabel}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Properties processed
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography variant="body2" textAlign="right">
              {propDone}
              {propTotal ? ` / ${propTotal}` : ""}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Tables processed
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography variant="body2" textAlign="right">
              {tableDone}
              {tableTotal ? ` / ${tableTotal}` : ""}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* Validation counts */}
        <Grid container spacing={1}>
          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Product line validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{ color: plValidations > 0 ? "error.main" : "text.primary" }}
            >
              {plValidations}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Property validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{
                color: propValidations > 0 ? "error.main" : "text.primary",
              }}
            >
              {propValidations}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Table validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{
                color: tableValidations > 0 ? "error.main" : "text.primary",
              }}
            >
              {tableValidations}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* Show Validations button */}
        <ShowValidationsButton />
      </Stack>
    );
  };

  return (
    <>
      <ActionButtonConfirmStatusWithPolling<
        ValidationProcess,
        ValidationProcess
      >
        // Button
        variant="outlined"
        color="primary"
        disabled={disabled}
        icon={TaskAltIcon}
        iconOnly={false}
        tooltipText="Validate Product Line"
        tooltipArrow
        tooltipPlacement="right"
        // Dialog
        maxWidth="xs"
        fullWidth
        dialogTitle="Validate Product Line"
        disableEscapeKeyDown
        disableCloseOnBackdropClick
        contentSection={contentSection}
        hideContentOnProcessComplete
        // Action buttons in dialog
        defaultButton="OK"
        cancelButtonText="Cancel"
        okButtonText="Validate"
        // Polling
        processPollingOptions={processPollingOptions}
        buildResultSection={buildResultSection}
        onDialogOpen={onDialogOpen}
        resetOnOpen
      >
        Validate Product Line
      </ActionButtonConfirmStatusWithPolling>
    </>
  );
};

/** styles */
const ContentStack = styled(Stack)`
  gap: 12px;
`;
